<script setup lang="ts">
import { ref, watch } from "vue";
import {
  UiButton,
  UiDialog,
  UiIcon,
  UiIconButton,
  UiInputField,
  UiText,
  useToaster,
} from "@shopexperts/shared";

import {
  createConfidenceSignalLabelVariant,
  deleteConfidenceSignalLabelVariant,
  listConfidenceSignalLabelVariants,
  updateConfidenceSignalLabelVariant,
  type ConfidenceSignalLabelVariant,
} from "@/app/services/confidenceSignalsApi.service";

const props = defineProps<{
  /** The definition being reworded; null while the dialog is closed. */
  signal: { key: string; name: string } | null;
}>();

const emit = defineEmits<{ (e: "changed"): void }>();

const open = defineModel<boolean>({ default: false });

const toaster = useToaster();
const variants = ref<ConfidenceSignalLabelVariant[]>([]);
/** The signal's own text, straight from the server rather than a dirty draft. */
const publicText = ref("");
const savedLabels = ref(new Map<string, string>());
const isLoading = ref(false);
const loadFailed = ref(false);
const newLabel = ref("");
const isCreating = ref(false);
const savingKey = ref<string | null>(null);
const reorderingKey = ref<string | null>(null);
const deletingKey = ref<string | null>(null);
const confirmDeleteKey = ref<string | null>(null);

/** Bumped per load so a slow response from a previous open is discarded. */
let loadToken = 0;

const isBusy = () =>
  isLoading.value ||
  isCreating.value ||
  savingKey.value !== null ||
  reorderingKey.value !== null ||
  deletingKey.value !== null;

watch(open, (isOpen) => {
  if (!isOpen) {
    confirmDeleteKey.value = null;
    return;
  }

  newLabel.value = "";
  confirmDeleteKey.value = null;
  // Clear the previous signal's rows before loading this one. Variant keys are
  // only unique within their signal, so leaving them on screen would let a save
  // or delete pair a stale row with the new signal's key and hit a different
  // record entirely.
  variants.value = [];
  publicText.value = "";
  savedLabels.value = new Map();
  loadFailed.value = false;
  void load();
});

/**
 * Reload rather than patch in place: a reorder rewrites several rows and a
 * create derives its key server-side, so the response list is the only
 * trustworthy order. Labels the admin has typed but not saved survive it.
 */
async function load() {
  const signal = props.signal;
  if (!signal) return;

  const dirty = new Map(
    variants.value.filter(isDirty).map((variant) => [variant.key, variant.label] as const),
  );

  const token = ++loadToken;
  /** True while this response is still the one the dialog is waiting for. */
  const isCurrent = () => token === loadToken && props.signal?.key === signal.key;

  isLoading.value = true;
  loadFailed.value = false;
  try {
    const result = await listConfidenceSignalLabelVariants(signal.key);
    if (!isCurrent()) return;

    const fresh = result.variants.map((variant) => ({ ...variant }));
    publicText.value = result.publicText;
    // Snapshots describe what the server holds, so a preserved edit stays dirty.
    savedLabels.value = new Map(fresh.map((variant) => [variant.key, variant.label]));

    for (const variant of fresh) {
      const label = dirty.get(variant.key);
      if (label !== undefined) variant.label = label;
    }

    variants.value = fresh;
  } catch (error) {
    if (isCurrent()) loadFailed.value = true;
    console.error("[ConfidenceSignals] Failed to load label variants", error);
  } finally {
    // A newer load owns the flag from the moment it starts.
    if (token === loadToken) isLoading.value = false;
  }
}

function isDirty(variant: ConfidenceSignalLabelVariant) {
  return savedLabels.value.get(variant.key) !== variant.label;
}

function canMove(variant: ConfidenceSignalLabelVariant, direction: "up" | "down") {
  const index = variants.value.findIndex((item) => item.key === variant.key);
  const target = index + (direction === "up" ? -1 : 1);
  return index >= 0 && target >= 0 && target < variants.value.length;
}

async function createVariant() {
  const signal = props.signal;
  const submitted = newLabel.value;
  const label = submitted.trim();
  if (!signal || isBusy()) return;

  if (!label) {
    toaster.create({ title: "A variant needs a label", type: "error" });
    return;
  }

  isCreating.value = true;
  try {
    // `key` is omitted on purpose — the API derives it from the label and
    // resolves collisions within the signal. `sort_index` defaults to 0
    // server-side, so handing out the next position keeps the arrows useful
    // instead of leaving every variant tied at zero.
    const created = await createConfidenceSignalLabelVariant(signal.key, {
      label,
      sort_index: variants.value.length,
    });
    // Only if the field still holds what was sent — the admin may have started
    // typing the next variant while this one was in flight.
    if (newLabel.value === submitted) newLabel.value = "";
    await load();
    emit("changed");
    toaster.create({ title: `${created.label} added`, type: "success" });
  } catch (error) {
    console.error("[ConfidenceSignals] Failed to create label variant", error);
  } finally {
    isCreating.value = false;
  }
}

async function saveVariant(variant: ConfidenceSignalLabelVariant) {
  const signal = props.signal;
  if (!signal) return;

  const submitted = variant.label;
  const label = submitted.trim();
  if (!label) {
    toaster.create({ title: "A variant needs a label", type: "error" });
    return;
  }

  savingKey.value = variant.key;
  try {
    const saved = await updateConfidenceSignalLabelVariant(signal.key, variant.key, { label });
    // An edit made while the request was in flight stays, and stays dirty —
    // only the saved baseline moves.
    if (variant.label === submitted) variant.label = saved.label;
    savedLabels.value.set(variant.key, saved.label);
    emit("changed");
    toaster.create({
      // Rewording is shared: it reaches every expert showing this variant.
      title: variant.experts_count > 0
        ? `Reworded on ${variant.experts_count} expert${variant.experts_count === 1 ? "" : "s"}`
        : "Label variant updated",
      type: "success",
    });
  } catch (error) {
    console.error("[ConfidenceSignals] Failed to update label variant", error);
  } finally {
    savingKey.value = null;
  }
}

/**
 * Reassigns the whole list sequentially rather than swapping two values:
 * variants created before this screen existed all default to sort_index 0, so
 * a plain swap of equal indices would leave the order unchanged.
 */
async function moveVariant(variant: ConfidenceSignalLabelVariant, direction: "up" | "down") {
  const signal = props.signal;
  if (!signal || !canMove(variant, direction) || isBusy()) return;

  const ordered = [...variants.value];
  const index = ordered.findIndex((item) => item.key === variant.key);
  const target = index + (direction === "up" ? -1 : 1);
  const [moved] = ordered.splice(index, 1);
  ordered.splice(target, 0, moved!);

  const changed = ordered
    .map((item, position) => ({ item, position }))
    .filter(({ item, position }) => item.sort_index !== position);

  reorderingKey.value = variant.key;
  try {
    await Promise.all(
      changed.map(({ item, position }) =>
        updateConfidenceSignalLabelVariant(signal.key, item.key, { sort_index: position }),
      ),
    );
    emit("changed");
  } catch (error) {
    console.error("[ConfidenceSignals] Failed to reorder label variants", error);
  } finally {
    // Also on failure: the writes are independent, so a partial one must not
    // leave a stale local order on screen.
    reorderingKey.value = null;
    await load();
  }
}

async function confirmDelete(variant: ConfidenceSignalLabelVariant) {
  const signal = props.signal;
  if (!signal) return;

  deletingKey.value = variant.key;
  try {
    // Cascade only when experts actually show it, so deleting an unused variant
    // can never quietly reword anyone.
    await deleteConfidenceSignalLabelVariant(signal.key, variant.key, variant.experts_count > 0);
    confirmDeleteKey.value = null;
    await load();
    emit("changed");
    toaster.create({ title: `${variant.label} deleted`, type: "success" });
  } catch (error) {
    console.error("[ConfidenceSignals] Failed to delete label variant", error);
  } finally {
    deletingKey.value = null;
  }
}

function deleteWarning(variant: ConfidenceSignalLabelVariant) {
  if (variant.experts_count === 0) return "Not shown on any expert. Delete it?";
  return `Shown on ${variant.experts_count} expert${variant.experts_count === 1 ? "" : "s"} — they move back to the main label. Delete it?`;
}
</script>

<template>
  <UiDialog
    v-model="open"
    :title="signal ? `Label variants — ${signal.name}` : 'Label variants'"
    text="Alternate wordings for this signal. A variant changes only the published text, never its points, source, or scope — so choosing one can never move an expert's score."
    secondary-action-label="Done"
    size="xl"
    @secondary-action="open = false"
  >
    <div class="flex flex-col gap-4 text-left">
      <div class="rounded-lg border border-gray-200 bg-gray-50/70 px-4 py-3">
        <p class="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Main label</p>
        <p class="mt-0.5 text-sm text-gray-900">{{ publicText || '—' }}</p>
        <p class="mt-1 text-[11px] text-gray-500">
          Every expert shows this unless you point them at a variant from their Trust Signals tab.
        </p>
      </div>

      <div v-if="isLoading && variants.length === 0" class="py-10 text-center">
        <div class="mx-auto mb-3 h-7 w-7 animate-spin rounded-full border-2 border-gray-200 border-t-emerald-600" />
        <UiText as="p" variant="p-small" class="text-gray-500">Loading label variants...</UiText>
      </div>

      <div v-else-if="loadFailed" class="rounded-lg border border-red-200 bg-red-50 p-5 text-center">
        <UiText as="p" variant="p-small" class="text-red-700">Label variants could not be loaded.</UiText>
        <UiButton class="mt-3" variant="outline" label="Try again" @click="load()" />
      </div>

      <template v-else>
        <div
          v-if="variants.length === 0"
          class="rounded-lg border border-dashed border-gray-300 px-4 py-8 text-center"
        >
          <UiIcon name="pencil" class="mx-auto mb-2 h-6 w-6 text-gray-300" />
          <UiText as="p" variant="p-small" class="text-gray-500">
            No variants yet. Add one when a signal reads better with different wording on some experts.
          </UiText>
        </div>

        <div v-else class="overflow-hidden rounded-lg border border-gray-200">
          <!-- Fixed column widths, not `auto`: each row is its own grid, so a
               content-sized column would drift as a button changes its label. -->
          <div class="hidden gap-3 border-b border-gray-100 bg-gray-50/80 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-gray-400 sm:grid sm:grid-cols-[minmax(0,1fr)_8rem_5.5rem_9.5rem]">
            <span>Label</span>
            <span>Key</span>
            <span class="text-center">Order</span>
            <span class="text-right">Actions</span>
          </div>

          <div class="divide-y divide-gray-100">
            <div v-for="variant in variants" :key="variant.key" class="px-4 py-3">
              <div class="grid gap-3 sm:grid-cols-[minmax(0,1fr)_8rem_5.5rem_9.5rem] sm:items-center">
                <div class="min-w-0">
                  <label :for="`variant-${variant.key}-label`" class="sr-only">Variant label</label>
                  <UiInputField
                    v-model="variant.label"
                    :name="`variant-${variant.key}-label`"
                    placeholder="Storetasker listing"
                  />
                </div>

                <div class="min-w-0">
                  <p class="truncate font-mono text-[11px] text-gray-600" :title="variant.key">
                    {{ variant.key }}
                  </p>
                  <p class="mt-0.5 text-[11px] text-gray-400">
                    {{ variant.experts_count }} expert{{ variant.experts_count === 1 ? '' : 's' }}
                  </p>
                </div>

                <div class="flex items-center gap-1 sm:justify-center">
                  <UiIconButton
                    icon="chevron-down"
                    variant="ghost"
                    class="h-9 w-9 rotate-180 border border-gray-200"
                    :aria-label="`Move ${variant.label} up`"
                    :disabled="!canMove(variant, 'up') || reorderingKey !== null"
                    @click="moveVariant(variant, 'up')"
                  />
                  <UiIconButton
                    icon="chevron-down"
                    variant="ghost"
                    class="h-9 w-9 border border-gray-200"
                    :aria-label="`Move ${variant.label} down`"
                    :disabled="!canMove(variant, 'down') || reorderingKey !== null"
                    @click="moveVariant(variant, 'down')"
                  />
                </div>

                <div class="flex items-center justify-end gap-1">
                  <UiButton
                    variant="primary"
                    class="flex-1"
                    :label="savingKey === variant.key ? 'Saving...' : 'Save'"
                    :disabled="!isDirty(variant) || savingKey === variant.key"
                    @click="saveVariant(variant)"
                  />
                  <UiButton
                    variant="ghost"
                    shape="square"
                    :title="`Delete ${variant.label}`"
                    :aria-label="`Delete ${variant.label}`"
                    :disabled="deletingKey === variant.key"
                    @click="confirmDeleteKey = variant.key"
                  >
                    <template #before_label><UiIcon name="delete" class="h-4 w-4 text-red-600" /></template>
                  </UiButton>
                </div>
              </div>

              <div
                v-if="confirmDeleteKey === variant.key"
                class="mt-3 flex flex-col gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
              >
                <UiText as="p" variant="p-small" class="text-amber-900">
                  {{ deleteWarning(variant) }}
                </UiText>
                <div class="flex shrink-0 items-center gap-2">
                  <UiButton
                    variant="outline"
                    label="Cancel"
                    :disabled="deletingKey === variant.key"
                    @click="confirmDeleteKey = null"
                  />
                  <UiButton
                    variant="primary"
                    :label="deletingKey === variant.key ? 'Deleting...' : 'Delete variant'"
                    :disabled="deletingKey === variant.key"
                    @click="confirmDelete(variant)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="rounded-lg border border-gray-200 px-4 py-3">
          <label for="new-label-variant" class="block text-xs font-semibold uppercase tracking-wide text-gray-500">
            New variant
          </label>
          <!-- Above the field, not under it: a line under the input would make
               this column taller than the button and knock the two out of line. -->
          <p class="mb-1.5 mt-0.5 text-[11px] text-gray-400">
            The key is derived from the label and cannot be changed later.
          </p>
          <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div class="min-w-0 flex-1">
              <UiInputField
                v-model="newLabel"
                name="new-label-variant"
                placeholder="Storetasker listing"
                @keyup.enter="createVariant"
              />
            </div>
            <UiButton
              variant="outline"
              class="shrink-0"
              :label="isCreating ? 'Adding...' : 'Add variant'"
              :disabled="isCreating || isLoading"
              @click="createVariant"
            >
              <template #before_label><UiIcon name="plus" class="h-4 w-4" /></template>
            </UiButton>
          </div>
        </div>
      </template>
    </div>
  </UiDialog>
</template>
